import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchBrands} from 'api';
import {Page} from 'components/common/Page';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect, Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {isAdmin$} from 'models/auth';
import {
    storageLocationsAddresses$,
    storageLocationsNames$,
} from 'models/virtualWarehouse/grid/table';
import {breakpoint} from 'theme';
import {
    GridFieldConfig,
    VirtualWarehouseGridAccessors,
    VirtualWarehouseGridFormattedRow,
    VirtualWarehouseGridRawRow,
} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';
import {mapEnumToI18nOptions} from 'utils/select';

import {Navigation} from '../Navigation';

import {VirtualWarehouseGridList} from './List';
import {BalanceCell, CodesCountCell, HeaderControls} from './styled';
import {VirtualWarehouseGridTable} from './Table';

export function convertData(
    data: VirtualWarehouseGridRawRow[] | undefined,
    t: TFunction,
): VirtualWarehouseGridFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [VirtualWarehouseGridAccessors.participant]: handleNil(elem.participant),
        [VirtualWarehouseGridAccessors.name]: handleNil(elem.productName),
        [VirtualWarehouseGridAccessors.gtin]: handleNil(elem.gtin),
        [VirtualWarehouseGridAccessors.code]: handleNil(elem.code),
        [VirtualWarehouseGridAccessors.type]: handleNil(elem.type),
        [VirtualWarehouseGridAccessors.income]: handleNil(elem.incomeCount, (value: number) =>
            formatNumber(value),
        ),
        [VirtualWarehouseGridAccessors.expense]: handleNil(elem.expenseCount, (value: number) =>
            formatNumber(value),
        ),
        [VirtualWarehouseGridAccessors.trademark]: handleNil(elem.productTrademark),
        [VirtualWarehouseGridAccessors.groupCode]: handleNil(elem.productGroupCode, (value) => (
            <ProductGroupIcon value={value} label />
        )),
        [VirtualWarehouseGridAccessors.balance]: handleNil(elem.remainCount, (value: number) => (
            <BalanceCell isNegative={value < 0}>{formatNumber(value)}</BalanceCell>
        )),
        [VirtualWarehouseGridAccessors.balanceStart]: handleNil(
            elem.remainStartCount,
            (value: number) => (
                <BalanceCell isNegative={value < 0}>{formatNumber(value)}</BalanceCell>
            ),
        ),
        [VirtualWarehouseGridAccessors.storageName]: elem.storageName,
        [VirtualWarehouseGridAccessors.storageAddress]: elem.storageAddress,
        [VirtualWarehouseGridAccessors.owner]: handleNil(elem.owner),
        [VirtualWarehouseGridAccessors.codesCount]: handleNil(elem.codesCount, (value) => (
            <CodesCountCell>{value}</CodesCountCell>
        )),
    }));
}

export const VirtualWarehouseGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const hasParticipantViewPermission = useStore(isAdmin$);

    const getGridColumns = (t: TFunction, brands: any[], groups: any[]): GridFieldConfig[] => [
        {
            id: VirtualWarehouseGridAccessors.groupCode,
            header: t('pages.virtualWarehouse.groupCode'),
            filter: (
                <Select
                    options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                    isClearable
                />
            ),
            width: 160,
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.name,
            header: t('pages.virtualWarehouse.name'),
            filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: true,
        },
        {
            id: VirtualWarehouseGridAccessors.gtin,
            header: t('pages.virtualWarehouse.gtin'),
            // (
            // <GtinHeader>
            //     <SortingIndicatorIcon
            //         fillColor={
            //             !gtinCollapsed ? lighten(0.1, colors.textSecondary) : colors.textPrimary
            //         }
            //         isSortedDesc={true}
            //         source={SortIcon}
            //         width={20}
            //         height={20}
            //         block={false}
            //         onClick={() => gtinCollapsedChanged(!gtinCollapsed)}
            //     />
            //     {t('pages.virtualWarehouse.gtin')}
            // </GtinHeader>
            // ),
            // filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
            isSortable: true,
        },
        {
            id: VirtualWarehouseGridAccessors.trademark,
            header: t('pages.virtualWarehouse.trademark'),
            filter: (
                <AsyncSelect
                    name={'trademark-selector'}
                    defaultOptions
                    isSearchable={false}
                    loadOptions={async () => {
                        const value = await fetchBrands();
                        return value
                            .sort((a: string, b: string) => a.localeCompare(b))
                            .map((item: string) => ({
                                value: item,
                                label: item,
                            }));
                    }}
                    isClearable
                />
            ),
            isSortable: false,
        },
        {
            id: VirtualWarehouseGridAccessors.codesCount,
            header: t('pages.virtualWarehouse.codesCount'),
            isSortable: true,
        },
        {
            id: VirtualWarehouseGridAccessors.storageName,
            header: t('pages.virtualWarehouse.storageName'),
            filter: (
                <CheckboxArray
                    searchable={true}
                    placeholder="Enter the name or address"
                    name="storage-location-filter"
                    options={() => storageLocationsNames$.getState()}
                />
            ),
            isSortable: true,
        },
        {
            id: VirtualWarehouseGridAccessors.storageAddress,
            header: t('pages.virtualWarehouse.storageAddress'),
            filter: (
                <CheckboxArray
                    searchable={true}
                    placeholder="Enter the name or address"
                    name="storage-location-filter"
                    options={() => storageLocationsAddresses$.getState()}
                />
            ),
            isSortable: true,
        },
        ...(hasParticipantViewPermission
            ? [
                  {
                      id: VirtualWarehouseGridAccessors.owner,
                      header: t('pages.virtualWarehouse.owner'),
                      filter: <Input placeholder={t('pages.virtualWarehouse.enterValue')} />,
                      isSortable: true,
                  },
              ]
            : []),
    ];

    const pageHeaderControls = undefined;
    // isMobile ? (
    //     <Icon
    //         source={FilterIcon}
    //         width={18}
    //         height={18}
    //         onClick={() => showConfigBlockChanged(!showConfigBlock)}
    //         fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
    //     />
    // ) : (
    //     undefined
    // );

    const headerControls = (
        <HeaderControls>
            <Navigation />
        </HeaderControls>
    );

    return (
        <Page title={t('menu.virtualWarehouse')} controls={pageHeaderControls}>
            {!isMobile && (
                <VirtualWarehouseGridTable
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                    headerControls={headerControls}
                />
            )}
            {isMobile && (
                <VirtualWarehouseGridList
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                    headerControls={headerControls}
                    hasParticipantViewPermission={hasParticipantViewPermission}
                />
            )}
        </Page>
    );
};
